import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import {
  FaPlus,
  FaEdit,
  FaTrash,
  FaToggleOn,
  FaToggleOff,
  FaTicketAlt,
  FaEye,
  FaSearch,
} from "react-icons/fa";
import axios from "axios";
import { API_URL } from "../../config/apiConfig";

const CouponManagement = () => {
  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingCoupon, setEditingCoupon] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterActive, setFilterActive] = useState("all");
  const [pagination, setPagination] = useState({
    current: 1,
    pages: 1,
    total: 0,
  });

  // Determine coupon display status based on active state, expiry, and usage limits
  const getCouponStatus = (coupon) => {
    if (!coupon.isActive) {
      return {
        text: "Inactive",
        className: "bg-red-100 text-red-800",
      };
    }
    const isExpired = new Date(coupon.validUntil) < new Date();
    if (isExpired) {
      return {
        text: "Expired",
        className: "bg-gray-100 text-gray-800 border border-gray-200",
      };
    }
    const isLimitReached = coupon.usageLimit !== null && coupon.usedCount >= coupon.usageLimit;
    if (isLimitReached) {
      return {
        text: "Limit Reached",
        className: "bg-amber-100 text-amber-800",
      };
    }
    return {
      text: "Active",
      className: "bg-green-100 text-green-800",
    };
  };


  // Form state
  const [formData, setFormData] = useState({
    code: "",
    description: "",
    discountType: "percentage",
    discountValue: "",
    minimumBookingAmount: "",
    maximumDiscountAmount: "",
    usageLimit: "",
    userUsageLimit: "1",
    validUntil: "",
    applicableCabTypes: ["all"],
  });

  // Get auth token
  const getAuthToken = () => {
    const user = JSON.parse(localStorage.getItem("user") || "{}");
    return user?.token;
  };

  // Create axios instance with auth
  const createApiClient = () => {
    const token = getAuthToken();
    return axios.create({
      baseURL: API_URL,
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
    });
  };

  // Fetch coupons
  const fetchCoupons = async (page = 1, search = "", active = "all") => {
    try {
      setLoading(true);
      const api = createApiClient();

      const params = new URLSearchParams({
        page: page.toString(),
        limit: "10",
      });

      if (search) params.append("search", search);
      if (active !== "all") params.append("isActive", active);

      const response = await api.get(`/coupons?${params}`);

      if (response.data.success) {
        setCoupons(response.data.coupons);
        setPagination(response.data.pagination);
      }
    } catch (error) {
      console.error("Error fetching coupons:", error);
    } finally {
      setLoading(false);
    }
  };

  // Load coupons on component mount
  useEffect(() => {
    fetchCoupons();
  }, []);

  // Handle search and filter
  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      fetchCoupons(1, searchTerm, filterActive);
    }, 500);

    return () => clearTimeout(delayDebounceFn);
  }, [searchTerm, filterActive]);

  // Reset form
  const resetForm = () => {
    setFormData({
      code: "",
      description: "",
      discountType: "percentage",
      discountValue: "",
      minimumBookingAmount: "",
      maximumDiscountAmount: "",
      usageLimit: "",
      userUsageLimit: "1",
      validUntil: "",
      applicableCabTypes: ["all"],
    });
    setEditingCoupon(null);
  };

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;

    if (name === "applicableCabTypes") {
      if (checked) {
        setFormData((prev) => ({
          ...prev,
          applicableCabTypes: [...prev.applicableCabTypes, value],
        }));
      } else {
        setFormData((prev) => ({
          ...prev,
          applicableCabTypes: prev.applicableCabTypes.filter(
            (type) => type !== value,
          ),
        }));
      }
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: type === "checkbox" ? checked : value,
      }));
    }
  };

  // Create or update coupon
  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      const api = createApiClient();

      // Prepare data
      const submitData = {
        ...formData,
        code: formData.code.toUpperCase(),
        discountValue: parseFloat(formData.discountValue),
        minimumBookingAmount: formData.minimumBookingAmount
          ? parseFloat(formData.minimumBookingAmount)
          : 0,
        maximumDiscountAmount: formData.maximumDiscountAmount
          ? parseFloat(formData.maximumDiscountAmount)
          : null,
        usageLimit: formData.usageLimit ? parseInt(formData.usageLimit) : null,
        userUsageLimit: parseInt(formData.userUsageLimit),
      };

      if (editingCoupon) {
        // Update existing coupon
        await api.put(`/coupons/${editingCoupon._id}`, submitData);
        toast.success("Coupon updated successfully");
      } else {
        // Create new coupon
        await api.post("/coupons", submitData);
        toast.success("Coupon created successfully");
      }

      setShowModal(false);
      resetForm();
      fetchCoupons(pagination.current, searchTerm, filterActive);
    } catch (error) {
      const errorMessage = error.response?.data?.message || "Operation failed";
      toast.error(errorMessage);
    }
  };

  // Edit coupon
  const handleEdit = (coupon) => {
    setEditingCoupon(coupon);
    setFormData({
      code: coupon.code,
      description: coupon.description,
      discountType: coupon.discountType,
      discountValue: coupon.discountValue.toString(),
      minimumBookingAmount: coupon.minimumBookingAmount?.toString() || "",
      maximumDiscountAmount: coupon.maximumDiscountAmount?.toString() || "",
      usageLimit: coupon.usageLimit?.toString() || "",
      userUsageLimit: coupon.userUsageLimit.toString(),
      validUntil: new Date(coupon.validUntil).toISOString().split("T")[0],
      applicableCabTypes: coupon.applicableCabTypes || ["all"],
    });
    setShowModal(true);
  };

  // Toggle coupon status
  const handleToggleStatus = async (couponId) => {
    try {
      const api = createApiClient();
      await api.patch(`/coupons/${couponId}/toggle`);
      toast.success("Coupon status updated");
      fetchCoupons(pagination.current, searchTerm, filterActive);
    } catch (error) {
      toast.error("Failed to update coupon status");
    }
  };

  // Delete coupon
  const handleDelete = async (couponId) => {
    if (!window.confirm("Are you sure you want to delete this coupon?")) {
      return;
    }

    try {
      const api = createApiClient();
      await api.delete(`/coupons/${couponId}`);
      toast.success("Coupon deleted successfully");
      fetchCoupons(pagination.current, searchTerm, filterActive);
    } catch (error) {
      const errorMessage =
        error.response?.data?.message || "Failed to delete coupon";
      toast.error(errorMessage);
    }
  };

  // Format discount display
  const formatDiscount = (coupon) => {
    if (coupon.discountType === "percentage") {
      return `${coupon.discountValue}% off`;
    } else {
      return `₹${coupon.discountValue} off`;
    }
  };

  return (
    <div className="coupon-management p-6">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-800 flex items-center">
            <FaTicketAlt className="mr-2 text-amber-600" />
            Coupon Management
          </h1>
          <p className="text-gray-600 mt-1">
            Manage promotional codes and discounts
          </p>
        </div>
        <button
          onClick={() => {
            resetForm();
            setShowModal(true);
          }}
          className="bg-amber-600 text-white px-4 py-2 rounded-lg flex items-center hover:bg-amber-700 transition-colors"
        >
          <FaPlus className="mr-2" />
          Add Coupon
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-md p-4 mb-6">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Search coupons..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500"
              />
            </div>
          </div>
          <div>
            <select
              value={filterActive}
              onChange={(e) => setFilterActive(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500"
            >
              <option value="all">All Coupons</option>
              <option value="true">Active Only</option>
              <option value="false">Inactive Only</option>
            </select>
          </div>
        </div>
      </div>

      {/* Coupons Table */}
      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        {loading ? (
          <div className="flex justify-center items-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-600"></div>
          </div>
        ) : coupons.length === 0 ? (
          <div className="text-center py-12">
            <FaTicketAlt className="mx-auto text-gray-400 text-4xl mb-4" />
            <p className="text-gray-500">No coupons found</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Code
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Description
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Discount
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Usage
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Valid Until
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {coupons.map((coupon) => (
                  <tr key={coupon._id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="font-medium text-gray-900">
                        {coupon.code}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-900 max-w-xs truncate">
                        {coupon.description}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800">
                        {formatDiscount(coupon)}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {coupon.usedCount} / {coupon.usageLimit || "∞"}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {new Date(coupon.validUntil).toLocaleDateString("en-IN")}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {(() => {
                        const status = getCouponStatus(coupon);
                        return (
                          <span
                            className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${status.className}`}
                          >
                            {status.text}
                          </span>
                        );
                      })()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex space-x-2">
                        <button
                          onClick={() => handleEdit(coupon)}
                          className="text-blue-600 hover:text-blue-900"
                          title="Edit"
                        >
                          <FaEdit />
                        </button>
                        <button
                          onClick={() => handleToggleStatus(coupon._id)}
                          className={`${
                            coupon.isActive
                              ? "text-red-600 hover:text-red-900"
                              : "text-green-600 hover:text-green-900"
                          }`}
                          title={coupon.isActive ? "Deactivate" : "Activate"}
                        >
                          {coupon.isActive ? <FaToggleOff /> : <FaToggleOn />}
                        </button>
                        {coupon.usedCount === 0 && (
                          <button
                            onClick={() => handleDelete(coupon._id)}
                            className="text-red-600 hover:text-red-900"
                            title="Delete"
                          >
                            <FaTrash />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {pagination.pages > 1 && (
          <div className="px-6 py-3 border-t border-gray-200">
            <div className="flex justify-between items-center">
              <div className="text-sm text-gray-500">
                Showing {(pagination.current - 1) * 10 + 1} to{" "}
                {Math.min(pagination.current * 10, pagination.total)} of{" "}
                {pagination.total} results
              </div>
              <div className="flex space-x-2">
                {Array.from({ length: pagination.pages }, (_, i) => i + 1).map(
                  (page) => (
                    <button
                      key={page}
                      onClick={() =>
                        fetchCoupons(page, searchTerm, filterActive)
                      }
                      className={`px-3 py-1 text-sm rounded ${
                        page === pagination.current
                          ? "bg-amber-600 text-white"
                          : "bg-white text-gray-700 border border-gray-300 hover:bg-gray-50"
                      }`}
                    >
                      {page}
                    </button>
                  ),
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <h2 className="text-xl font-bold mb-4">
              {editingCoupon ? "Edit Coupon" : "Create New Coupon"}
            </h2>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Coupon Code *
                  </label>
                  <input
                    type="text"
                    name="code"
                    value={formData.code}
                    onChange={handleInputChange}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-amber-500"
                    placeholder="e.g., WELCOME10"
                    required
                    style={{ textTransform: "uppercase" }}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Discount Type *
                  </label>
                  <select
                    name="discountType"
                    value={formData.discountType}
                    onChange={handleInputChange}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-amber-500"
                    required
                  >
                    <option value="percentage">Percentage</option>
                    <option value="fixed">Fixed Amount</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Discount Value *
                  </label>
                  <input
                    type="number"
                    name="discountValue"
                    value={formData.discountValue}
                    onChange={handleInputChange}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-amber-500"
                    placeholder={
                      formData.discountType === "percentage" ? "10" : "100"
                    }
                    min="0"
                    max={
                      formData.discountType === "percentage" ? "100" : undefined
                    }
                    step={formData.discountType === "percentage" ? "1" : "0.01"}
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Valid Until *
                  </label>
                  <input
                    type="date"
                    name="validUntil"
                    value={formData.validUntil}
                    onChange={handleInputChange}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-amber-500"
                    min={new Date().toISOString().split("T")[0]}
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Minimum Booking Amount
                  </label>
                  <input
                    type="number"
                    name="minimumBookingAmount"
                    value={formData.minimumBookingAmount}
                    onChange={handleInputChange}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-amber-500"
                    placeholder="0"
                    min="0"
                    step="0.01"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Maximum Discount Amount
                  </label>
                  <input
                    type="number"
                    name="maximumDiscountAmount"
                    value={formData.maximumDiscountAmount}
                    onChange={handleInputChange}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-amber-500"
                    placeholder="No limit"
                    min="0"
                    step="0.01"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Total Usage Limit
                  </label>
                  <input
                    type="number"
                    name="usageLimit"
                    value={formData.usageLimit}
                    onChange={handleInputChange}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-amber-500"
                    placeholder="Unlimited"
                    min="1"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Per User Usage Limit *
                  </label>
                  <input
                    type="number"
                    name="userUsageLimit"
                    value={formData.userUsageLimit}
                    onChange={handleInputChange}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-amber-500"
                    min="1"
                    required
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description *
                </label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-amber-500"
                  rows="3"
                  placeholder="Describe the coupon offer..."
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Applicable Cab Types
                </label>
                <div className="flex flex-wrap gap-2">
                  {["all", "standard", "sedan", "innova", "suv", "luxury"].map(
                    (type) => (
                      <label key={type} className="flex items-center">
                        <input
                          type="checkbox"
                          name="applicableCabTypes"
                          value={type}
                          checked={formData.applicableCabTypes.includes(type)}
                          onChange={handleInputChange}
                          className="mr-2"
                        />
                        <span className="capitalize">{type}</span>
                      </label>
                    ),
                  )}
                </div>
              </div>

              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowModal(false);
                    resetForm();
                  }}
                  className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700"
                >
                  {editingCoupon ? "Update" : "Create"} Coupon
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default CouponManagement;
